"use client";

/* Motion runtime is a mutable class instance (ADR-011). */
/* eslint-disable react-hooks/immutability */

import { useCallback, useState } from "react";
import { ScrollTrigger } from "@/lib/motion/gsap";
import { ContactCover } from "@/components/sections/contact/ContactCover";
import { FakeThumbs } from "@/components/site/FakeThumbs";
import { Header } from "@/components/site/Header";
import { JumpMenu } from "@/components/site/JumpMenu";
import { Loader } from "@/components/site/Loader";
import { ProgressLines } from "@/components/site/ProgressLines";
import { ScrollSpine } from "@/components/site/ScrollSpine";
import { SideLeft } from "@/components/site/SideLeft";
import { SideRight } from "@/components/site/SideRight";
import { SiteOverlays } from "@/components/site/SiteOverlays";
import { WorkBackgrounds } from "@/components/site/WorkBackgrounds";
import { ServicesMosaic } from "@/components/sections/services/ServicesMosaic";
import { Story } from "@/components/sections/story/Story";
import { Contact } from "@/components/sections/contact/Contact";
import { Hero } from "@/components/sections/hero/Hero";
import { Products } from "@/components/sections/products/Products";
import { Projects } from "@/components/sections/work/Projects";
import { Services } from "@/components/sections/services/Services";
import { Work } from "@/components/sections/work/Work";
import { MotionContext } from "@/components/site/motion/MotionContext";
import { LandscapeLock } from "@/components/site/LandscapeLock";
import { PageBoot } from "@/components/site/motion/PageBoot";
import { SiteMotion } from "@/lib/motion/site-runtime";

export default function SiteExperience() {
  const [motion] = useState(() => new SiteMotion());
  const [epoch, setEpoch] = useState(0);
  const [loaderDone, setLoaderDone] = useState(false);
  const rebuildWaiters = useState(() => [] as Array<() => void>)[0];

  const arm = useCallback(() => setEpoch((n) => (n === 0 ? 1 : n)), []);
  const completeLoader = useCallback(() => setLoaderDone(true), []);
  const flushRebuildWaiters = useCallback(() => {
    const waiters = rebuildWaiters.splice(0, rebuildWaiters.length);
    waiters.forEach((resolve) => resolve());
  }, [rebuildWaiters]);

  const requestRebuild = useCallback(() => {
    motion.lock(false, { force: true });
    motion.resizing = true;
    if (motion.appResizeTimer) clearTimeout(motion.appResizeTimer);
    // Debounce: do NOT reset on every tick — that left clip-paths / type mid-morph while dragging.
    motion.appResizeTimer = setTimeout(() => {
      motion.setVh();
      motion.spine?.reset();
      motion.slider?.resize();
      motion.resizing = false;
      setEpoch((n) => n + 1);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          motion.spine?.placeThumbs();
          motion.refreshInviewCache();
          ScrollTrigger.refresh();
          motion.lock(false, { force: true });
          flushRebuildWaiters();
        });
      });
    }, 300);
  }, [motion, flushRebuildWaiters]);

  const requestRebuildAsync = useCallback(() => {
    return new Promise<void>((resolve) => {
      rebuildWaiters.push(resolve);
      requestRebuild();
    });
  }, [motion, rebuildWaiters, requestRebuild]);

  motion.requestRebuild = requestRebuild;
  motion.requestRebuildAsync = requestRebuildAsync;
  if (typeof window !== "undefined") {
    motion.isTouch =
      "ontouchstart" in window ||
      (navigator as Navigator & { msMaxTouchPoints?: number }).msMaxTouchPoints! > 0;
  }

  return (
    <MotionContext.Provider value={{ motion, epoch, arm, loaderDone, completeLoader }}>
      <PageBoot />
      <LandscapeLock />
      {!loaderDone && <Loader />}
      <Header />
      <FakeThumbs />
      <ContactCover />
      <WorkBackgrounds />
      <ProgressLines />
      <JumpMenu />
      <SiteOverlays />
      <div className="container_wrap full_page f s-b x_padding _tempHide">
        <ServicesMosaic />
        <SideLeft />
        <div className="centered f a-c j-c">
          <Hero />
          <Work />
          <Projects />
          <Services />
          <Products />
          <Story />
          <Contact />
        </div>
        <SideRight />
      </div>
      <ScrollSpine />
    </MotionContext.Provider>
  );
}
